import React from 'react';

import { UserData } from '@client/store/globalStore/state/initialGlobalState';
import i18n from '@client/utils/i18n';
import { ProjectStatus } from '@ledium/types';
import { CircadianChart, RecipeSections, WavelengthChart } from '@ledium/ui-common';
import FastForwardIcon from '@mui/icons-material/FastForward';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import LinkIcon from '@mui/icons-material/Link';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import StopIcon from '@mui/icons-material/Stop';
import { Box, Checkbox, Container, Grid, Link, Popover, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

import DashboardIndependentLinkIcon from './DashboardIndependentLinkIcon';
import DashboardLinkPopover from './DashboardLinkPopover';
import { DashboardProjectItem } from './DashboardProject';
import DliInfoPopper, { SectionDliInfo } from './DliInfoPopper';
import ProjectTime from './ProjectTime';

interface DashboardProjectViewProps {
    project: DashboardProjectItem;
    projectStatus: Partial<ProjectStatus>;
    chartData: Record<'pfd' | 'irradiance', Parameters<typeof CircadianChart>[0]['data']>;
    spectrumChartData: Partial<{
        chart: Array<{ waveLength: number; pfd: number; irradiance: number }>;
        maxIntensity: number;
        maxIrradianceIntensity: number;
        channelIrradiance: Record<string, number>;
    }>;
    startProject: (startTime: number | null) => void;
    stopProject: () => void;
    pauseProject: () => void;
    userData: UserData;
    jumpSection: (direction: 'back' | 'restart' | 'forward') => void;
    jumpWorking: boolean;
    currentDli: number | null;
    sectionDlis: SectionDliInfo[] | null;
    sectionDlisLoading: boolean;
    onRequestSectionDlis: () => void;
    mirrorChannelMap: Record<string, { name: string; dmxAddress: number }[]>;
}

interface RunningInfoProps {
    project: DashboardProjectItem;
    projectStatus: Partial<ProjectStatus>;
    isDelayed: boolean;
    spectrumChartData: DashboardProjectViewProps['spectrumChartData'];
    chartData: DashboardProjectViewProps['chartData'];
    currentDli: number | null;
    sectionDlis: SectionDliInfo[] | null;
    sectionDlisLoading: boolean;
    onRequestSectionDlis: () => void;
    mirrorChannelMap: Record<string, { name: string; dmxAddress: number }[]>;
}

interface MirrorLinkCellProps {
    entries: { name: string; dmxAddress: number }[];
}

const MirrorLinkCell = React.memo(function MirrorLinkCell({ entries }: MirrorLinkCellProps) {
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
    return (
        <>
            <span style={{ display: 'inline-flex' }} onMouseEnter={(e) => setAnchor(e.currentTarget)} onMouseLeave={() => setAnchor(null)}>
                <LinkIcon fontSize="inherit" aria-label="mirror link" sx={{ ml: 0.5, verticalAlign: 'middle', opacity: 0.6 }} />
            </span>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                sx={{ pointerEvents: 'none' }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Box sx={{ p: 1, fontSize: '0.75rem' }}>
                    <table>
                        <tbody>
                            {entries.map((entry) => (
                                <tr key={entry.name}>
                                    <td style={{ paddingRight: 8 }}>{entry.name}</td>
                                    <td>
                                        DMX <b>{entry.dmxAddress}</b>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Box>
            </Popover>
        </>
    );
});

function RunningInfo({
    project,
    projectStatus,
    isDelayed,
    spectrumChartData,
    chartData,
    currentDli,
    sectionDlis,
    sectionDlisLoading,
    onRequestSectionDlis,
    mirrorChannelMap
}: RunningInfoProps) {
    const [unit, setUnit] = React.useState<'pfd' | 'irradiance'>('pfd');

    return (
        <>
            <Grid item xs={10}>
                {isDelayed
                    ? i18n('project_is_starts_with_a_delay_within', <ProjectTime startTime={project.startTime!} />)
                    : i18n('project_is_running_for', <ProjectTime startTime={project.startTime!} />)}

                {projectStatus.section?.currentRepeatNum && !isDelayed ? (
                    <div>
                        {i18n(
                            'project_detailed_status',
                            <b>{projectStatus.section.name}</b>,
                            <b>{projectStatus.section.currentRepeatNum}</b>,
                            <b>{projectStatus.section.repeatNum}</b>,
                            <b>{projectStatus.section.sectionPercent}</b>
                        )}
                    </div>
                ) : null}
                {currentDli !== null && !isDelayed ? (
                    <div>
                        {i18n('dli')}
                        {': '}
                        <DliInfoPopper
                            currentDli={currentDli}
                            projectStatus={projectStatus}
                            sectionDlis={sectionDlis}
                            sectionDlisLoading={sectionDlisLoading}
                            onOpen={onRequestSectionDlis}
                        />
                    </div>
                ) : null}
            </Grid>

            {projectStatus?.channels?.length && !isDelayed && (
                <Grid item xs={12} md={6}>
                    <ToggleButtonGroup
                        color="primary"
                        value={unit}
                        exclusive
                        size="small"
                        onChange={function (_e, value) {
                            if (value !== null) {
                                setUnit(value);
                            }
                        }}
                        sx={{ mb: 1 }}
                    >
                        <ToggleButton value="pfd">{i18n('pfd')}</ToggleButton>
                        <ToggleButton value="irradiance">{i18n('irradiance')}</ToggleButton>
                    </ToggleButtonGroup>
                    <p>
                        {i18n('current_intensity')}
                        {': '}
                        <b>
                            {unit === 'pfd'
                                ? projectStatus.channels.reduce((a, c) => a + c.currentPfd, 0)
                                : projectStatus.channels.reduce((a, c) => a + (spectrumChartData.channelIrradiance?.[c.id] ?? 0), 0)}
                        </b>{' '}
                        {i18n(unit + '_unit')}
                    </p>
                    <table style={{ borderCollapse: 'collapse', fontSize: '13px' }}>
                        <tbody>
                            <tr>
                                <th style={{ border: '1px solid rgba(0,0,0,0.2)', padding: '4px 8px' }}>{i18n('light_channel')}</th>
                                <th style={{ border: '1px solid rgba(0,0,0,0.2)', padding: '4px 8px' }}>{i18n('dmx_channel')}</th>
                                <th style={{ border: '1px solid rgba(0,0,0,0.2)', padding: '4px 8px' }}>
                                    {i18n('intensity')} {i18n(unit + '_unit')}
                                </th>
                                <th style={{ border: '1px solid rgba(0,0,0,0.2)', padding: '4px 8px' }}>{i18n('value')}</th>
                            </tr>
                            {projectStatus.channels.map((channel, idx) => (
                                <tr key={idx}>
                                    <td style={{ border: '1px solid rgba(0,0,0,0.2)', padding: '4px 8px' }}>{channel.name}</td>
                                    <td style={{ border: '1px solid rgba(0,0,0,0.2)', padding: '4px 8px' }} align="center">
                                        <b>{channel.channelId}</b>, {i18n('X_bit', channel.resolution)}
                                        {mirrorChannelMap[channel.id] && <MirrorLinkCell entries={mirrorChannelMap[channel.id]} />}
                                    </td>
                                    <td style={{ border: '1px solid rgba(0,0,0,0.2)', padding: '4px 8px' }} align="right">
                                        <b>{unit === 'pfd' ? channel.currentPfd : (spectrumChartData.channelIrradiance?.[channel.id] ?? 0)}</b>{' '}
                                        {i18n(unit + '_unit')}
                                    </td>
                                    <td style={{ border: '1px solid rgba(0,0,0,0.2)', padding: '4px 8px' }} align="right">
                                        {channel.resolution > 1 ? channel.percent + '%' : i18n(channel.value ? 'one_bit_on' : 'one_bit_off')}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Grid>
            )}
            {spectrumChartData?.chart?.length && !isDelayed ? (
                <Grid item xs={12} md={6}>
                    <WavelengthChart
                        unit={unit}
                        data={spectrumChartData.chart as Parameters<typeof WavelengthChart>[0]['data']}
                        aspect={2}
                        animation={200}
                        yAxis={unit === 'pfd' ? spectrumChartData.maxIntensity : spectrumChartData.maxIrradianceIntensity}
                    />
                </Grid>
            ) : null}
            {chartData[unit].length && !isDelayed ? (
                <Grid item xs={12}>
                    <CircadianChart
                        data={chartData[unit]}
                        unit={unit}
                        referencePercent={projectStatus.section ? parseFloat(projectStatus.section.sectionPercent) : undefined}
                    />
                </Grid>
            ) : null}
        </>
    );
}

interface AdminControlsProps {
    project: DashboardProjectItem;
    projectStatus: Partial<ProjectStatus>;
    isDelayed: boolean;
    startProject: (startTime: number | null) => void;
    stopProject: () => void;
    pauseProject: () => void;
    jumpSection: (direction: 'back' | 'restart' | 'forward') => void;
    jumpWorking: boolean;
    onPendingStart: (pending: { startTime: number | null }) => void;
}

function AdminControls({
    project,
    projectStatus,
    isDelayed,
    startProject,
    stopProject,
    pauseProject,
    jumpSection,
    jumpWorking,
    onPendingStart
}: AdminControlsProps) {
    const nextTenMinWindowTs = Math.floor(+new Date() / 600000) * 600000 + 600000;
    const [delayCheck, setDelayCheck] = React.useState(false);
    const [startTime, setStartTime] = React.useState<Dayjs>(dayjs(nextTenMinWindowTs));

    const canStart = !project.running && !project.pauseData && !project.independentLink?.disabledReason;
    const isRunningOrPaused = project.running || project.pauseData;

    return (
        <>
            {canStart && (
                <>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'center' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<PlayArrowIcon />}
                            onClick={() => {
                                const resolvedStart = delayCheck ? dayjs(startTime).unix() : null;
                                setDelayCheck(false);
                                if ((project.mirrorLinkErrorCount ?? 0) > 0) {
                                    onPendingStart({ startTime: resolvedStart });
                                } else {
                                    startProject(resolvedStart);
                                }
                            }}
                        >
                            {i18n('start_project')}
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'center' }}>
                        <FormControlLabel
                            onChange={(event) => {
                                setDelayCheck((event as React.ChangeEvent<HTMLInputElement>).target.checked);
                            }}
                            control={<Checkbox />}
                            label={i18n('set_start_time')}
                        />
                        {delayCheck && (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    timeSteps={{ hours: 1, minutes: 10 }}
                                    ampm={false}
                                    value={startTime}
                                    onChange={(newValue) => setStartTime(newValue ?? dayjs())}
                                />
                            </LocalizationProvider>
                        )}
                    </Grid>
                </>
            )}
            {isRunningOrPaused && (
                <>
                    <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                        {projectStatus && !project.pauseData && !isDelayed ? (
                            <ButtonGroup variant="contained">
                                {projectStatus.prevSection?.valid ? (
                                    <Tooltip
                                        title={i18n('jump_back', projectStatus.prevSection.name, projectStatus.prevSection.repeatNum)}
                                        placement="top"
                                        arrow
                                    >
                                        <Button disabled={jumpWorking} onClick={() => jumpSection('back')}>
                                            <FastRewindIcon />
                                        </Button>
                                    </Tooltip>
                                ) : (
                                    <Button disabled>
                                        <FastRewindIcon />
                                    </Button>
                                )}
                                <Tooltip title={i18n('restart_current_circadian')} placement="top" arrow>
                                    <Button disabled={jumpWorking} onClick={() => jumpSection('restart')}>
                                        <ReplayIcon />
                                    </Button>
                                </Tooltip>
                                {projectStatus.nextSection?.valid ? (
                                    <Tooltip
                                        title={i18n('jump_forward', projectStatus.nextSection.name, projectStatus.nextSection.repeatNum)}
                                        placement="top"
                                        arrow
                                    >
                                        <Button disabled={jumpWorking} onClick={() => jumpSection('forward')}>
                                            <FastForwardIcon />
                                        </Button>
                                    </Tooltip>
                                ) : (
                                    <Button disabled>
                                        <FastForwardIcon />
                                    </Button>
                                )}
                            </ButtonGroup>
                        ) : null}
                    </Grid>
                    {!isDelayed && (
                        <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                            <Button variant="contained" startIcon={project.pauseData ? <PlayArrowIcon /> : <PauseIcon />} onClick={pauseProject}>
                                {project.pauseData ? i18n('resume_project') : i18n('pause_project')}
                            </Button>
                        </Grid>
                    )}
                    <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                        <Button variant="contained" color="secondary" startIcon={<StopIcon />} onClick={stopProject}>
                            {i18n('stop_project')}
                        </Button>
                    </Grid>
                </>
            )}
        </>
    );
}

const DashboardProjectView = ({
    project,
    projectStatus,
    chartData,
    startProject,
    stopProject,
    pauseProject,
    userData,
    jumpSection,
    jumpWorking,
    spectrumChartData,
    currentDli,
    sectionDlis,
    sectionDlisLoading,
    onRequestSectionDlis,
    mirrorChannelMap
}: DashboardProjectViewProps) => {
    const [pendingStart, setPendingStart] = React.useState<{ startTime: number | null } | null>(null);
    let isDelayed = false;
    if (project.running) {
        isDelayed = Date.now() / 1000 - project.startTime! < 0;
    }

    return (
        <Paper sx={{ pb: 2 }} elevation={3}>
            <Container>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h2">{`${i18n('project')}: ${project.name}`}</Typography>
                            {(project.linkCount ?? 0) > 0 && <DashboardLinkPopover project={project} />}
                            {project.independentLink && <DashboardIndependentLinkIcon independentLink={project.independentLink} />}
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1">{project.description}</Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <RecipeSections
                            status={project.running ? projectStatus.section : project.pauseData && project.pauseData.pauseStatus.section}
                            sectionList={project.recipe?.sectionList ?? []}
                            passive={true}
                        />
                    </Grid>

                    {project.running && (
                        <RunningInfo
                            project={project}
                            projectStatus={projectStatus}
                            isDelayed={isDelayed}
                            spectrumChartData={spectrumChartData}
                            chartData={chartData}
                            currentDli={currentDli}
                            sectionDlis={sectionDlis}
                            sectionDlisLoading={sectionDlisLoading}
                            onRequestSectionDlis={onRequestSectionDlis}
                            mirrorChannelMap={mirrorChannelMap}
                        />
                    )}

                    {project.pauseData && (
                        <Grid item xs={12}>
                            {i18n('project_is_paused_at', <ProjectTime startTime={project.startTime!} pausedTime={project.pauseData.time} />)}
                            <div>
                                {i18n(
                                    'project_detailed_last_status',
                                    <b>{project.pauseData.pauseStatus.section.name}</b>,
                                    <b>{project.pauseData.pauseStatus.section.currentRepeatNum}</b>,
                                    <b>{project.pauseData.pauseStatus.section.repeatNum}</b>,
                                    <b>{project.pauseData.pauseStatus.section.sectionPercent}</b>
                                )}
                            </div>
                        </Grid>
                    )}

                    {userData?.admin && (
                        <AdminControls
                            project={project}
                            projectStatus={projectStatus}
                            isDelayed={isDelayed}
                            startProject={startProject}
                            stopProject={stopProject}
                            pauseProject={pauseProject}
                            jumpSection={jumpSection}
                            jumpWorking={jumpWorking}
                            onPendingStart={setPendingStart}
                        />
                    )}
                </Grid>
            </Container>
            <Dialog open={pendingStart !== null} onClose={() => setPendingStart(null)} maxWidth="sm" fullWidth>
                {pendingStart !== null && (
                    <>
                        <DialogTitle>{i18n('start_project_mapping_error_title')}</DialogTitle>
                        <DialogContent>
                            <DialogContentText>{i18n('start_project_mapping_error_body', project.mirrorLinkErrorCount ?? 1)}</DialogContentText>
                            <DialogContentText sx={{ mt: 1 }}>
                                {i18n('start_project_mapping_error_fix', <Link href="/zones">{i18n('start_project_mapping_error_zones_link')}</Link>)}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setPendingStart(null)}>{i18n('cancel')}</Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<PlayArrowIcon />}
                                onClick={() => {
                                    startProject(pendingStart.startTime);
                                    setPendingStart(null);
                                }}
                            >
                                {i18n('start_project')}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Paper>
    );
};

export default React.memo(DashboardProjectView);
